import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { Layers, Calendar, Clock, Plus, CheckCircle, AlertCircle } from 'lucide-react';

function BatchPlan() {
  const { API } = useContext(AppContext);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    // Mock data for demonstration
    setBatches([
      { id: 'BATCH-2025-001', product: 'Strip Type A', quantity: 5000, startTime: '06:00', endTime: '10:00', line: 'Line A', operator: 'Rajesh Kumar', status: 'Completed', date: '2025-02-01' },
      { id: 'BATCH-2025-002', product: 'Strip Type B', quantity: 3500, startTime: '10:30', endTime: '14:00', line: 'Line A', operator: 'Amit Singh', status: 'In Progress', date: '2025-02-01' },
      { id: 'BATCH-2025-003', product: 'Strip Type A', quantity: 4500, startTime: '06:00', endTime: '10:00', line: 'Line B', operator: 'Priya Sharma', status: 'Completed', date: '2025-02-01' },
      { id: 'BATCH-2025-004', product: 'Strip Type C', quantity: 4000, startTime: '14:30', endTime: '18:00', line: 'Line A', operator: 'Vikram Patel', status: 'Scheduled', date: '2025-02-01' },
      { id: 'BATCH-2025-005', product: 'Strip Type B', quantity: 3000, startTime: '14:00', endTime: '18:00', line: 'Line B', operator: 'Meera Gupta', status: 'Scheduled', date: '2025-02-01' },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Scheduled': return 'bg-purple-100 text-purple-700';
      case 'Delayed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircle className="text-green-500" size={16} />;
      case 'In Progress': return <Clock className="text-blue-500" size={16} />;
      case 'Scheduled': return <Calendar className="text-purple-500" size={16} />;
      case 'Delayed': return <AlertCircle className="text-red-500" size={16} />;
      default: return null;
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading batch data...</div>;
  }

  const completedCount = batches.filter(b => b.status === 'Completed').length;
  const inProgressCount = batches.filter(b => b.status === 'In Progress').length;
  const scheduledCount = batches.filter(b => b.status === 'Scheduled').length;

  return (
    <div className="space-y-6" data-testid="batch-plan-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Batch Plan</h1>
          <p className="text-gray-600 mt-1">Daily batch scheduling and tracking</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="date"
            defaultValue="2025-02-01"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2">
            <Plus size={16} />
            <span>Add Batch</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <p className="text-sm text-gray-600">Total Batches</p>
          <p className="text-2xl font-bold text-gray-900">{batches.length}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600">Completed</p>
          <p className="text-2xl font-bold text-green-700">{completedCount}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600">In Progress</p>
          <p className="text-2xl font-bold text-blue-700">{inProgressCount}</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
          <p className="text-sm text-purple-600">Scheduled</p>
          <p className="text-2xl font-bold text-purple-700">{scheduledCount}</p>
        </div>
      </div>

      {/* Timeline View */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Layers className="text-blue-600" size={20} />
            <h3 className="text-lg font-bold text-gray-900">Today's Batch Schedule</h3>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {batches.map((batch) => (
            <div key={batch.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
              <div className="w-20 text-center">
                <p className="text-sm font-mono text-gray-900">{batch.startTime}</p>
                <p className="text-xs text-gray-500">to {batch.endTime}</p>
              </div>
              
              <div className="w-2 h-12 rounded-full bg-gradient-to-b from-blue-500 to-blue-300"></div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-sm text-blue-600 font-medium">{batch.id}</span>
                  <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                    {getStatusIcon(batch.status)}
                    <span>{batch.status}</span>
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 mt-1">{batch.product}</p>
                <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                  <span>Qty: {batch.quantity.toLocaleString()}</span>
                  <span>Line: {batch.line}</span>
                  <span>Operator: {batch.operator}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BatchPlan;
